import React, { useCallback, useState, useContext } from 'react'
import styled from 'styled-components'
import qs from 'qs'
import { rem } from 'polished'
import setRegionCookie from 'cookies/setRegionCookie'
import { setGeoRedirect, Status } from 'storage/geo'
import GeoContext from 'contexts/geoContext'
import { formToObject } from 'lib/forms/formToObject'
import { getCreditRebookingDetails } from 'api/flights'
import { pushWithRegion } from 'actions/NavigationActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import { setFlightCreditData } from 'actions/FlightActions'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import RebookingError from 'components/Flights/CreditRebooking/RebookingError'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { RouteComponentProps, withRouter } from 'react-router'

const InputField = styled(TextInput)`
  span {
    &:first-child {
      font-weight: ${props => props.theme.font.primary.weight.bold};
    }
  }

  span[class^='InputWrap__StyledInputWrap'] {
    border-radius: ${props => props.theme.borderRadius.S};
    height: ${rem(48)};
    margin-top: ${rem(4)};
  }
`

const Loading = styled(LoadingIndicator)`
  padding: ${rem(12)};
`

const StyledForm = styled.form`
  > * {
    margin-top: ${rem(24)};
  }
`

interface CreditRebookingForm {
  email: string;
  bookingReference: string;
}
interface Props extends RouteComponentProps<{}> {
  isOpen: boolean;
}

function RebookingModalContent({ isOpen, location }: Props) {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<{ status: number, message: string } | null>(null)
  const { currentRegionCode } = useContext(GeoContext)

  const getBookingDetails = useCallback((email: string, bookingReference: string) => {
    setLoading(true)

    getCreditRebookingDetails(email, bookingReference).then(res => {
      setLoading(false)
      const { status, message, creditDetails } = res

      if (status === 200) {
        const queryParams = {
          ...creditDetails.passengerCounts,
          flightsCredit: true,
        }

        if (currentRegionCode !== creditDetails.region) {
          setRegionCookie(creditDetails.region)
          setGeoRedirect(Status.redirected)
          window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + creditDetails.region.toLowerCase()) + location.search
        } else {
          dispatch(setFlightCreditData(res.creditDetails))
          dispatch(pushWithRegion('/flights', qs.stringify(queryParams)))
        }
      } else {
        setError({ status, message })
      }
    }).catch(error => {
      setLoading(false)
      setError({
        status: 500,
        message: 'Error validating your booking details',
      })
      throw error
    })
  }, [currentRegionCode, location.pathname, location.search, dispatch])

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { email, bookingReference } = formToObject<CreditRebookingForm>(e.currentTarget)
    getBookingDetails(email, bookingReference)
  }, [getBookingDetails])

  return (
    <ModalBase isOpen={isOpen}>
      <StyledForm name="joinForm" onSubmit={onSubmit}>
        <ModalHeader
          title="Verify your booking"
          subtitle="Please enter your Luxury Escapes account email address linked to your flight credit booking."
        />
        <ModalBody>
          <ModalContent>
            <InputField
              required
              name="email"
              type="email"
              inputMode="email"
              label="Email address"
              placeholder="Enter email address"
              data-testid="credit-reservation-email"
            />
            <InputField
              required
              noValidationSpacing
              name="bookingReference"
              label="Booking reference"
              placeholder="Enter booking reference"
              data-testid="credit-reservation-booking"
            />
            {loading && <Loading inline />}
            {error && !loading && <RebookingError error={error}/>}
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: 'Continue',
            type: 'submit',
            disabled: loading,
          }}
        />
      </StyledForm>
    </ModalBase>
  )
}

export default withRouter(RebookingModalContent)
